import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  IconButton,
  Box,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { useGetAttachmentUrlMutation } from "@/features/Global/api";

interface AttachmentViewModalProps {
  open: boolean;
  onClose: () => void;
  attachment: any | null;
}

const AttachmentViewModal: React.FC<AttachmentViewModalProps> = ({
  open,
  onClose,
  attachment,
}) => {
  const { t } = useTranslation(["bugherd"]);
  const [attachmentUrl, setAttachmentUrl] = useState(null);
  const [postAttachmentKey, { isLoading }] = useGetAttachmentUrlMutation();

  useEffect(() => {
    if (
      attachment &&
      !attachment?.url?.startsWith("data:") &&
      !attachment?.url?.startsWith("https:")
    ) {
      postAttachmentKey({
        key: attachment?.url,
      }).then((res) => {
        if (res?.data?.data?.decryptedData?.url) {
          setAttachmentUrl(res?.data?.data?.decryptedData?.url);
        }
      });
    }
    if (attachment && attachment?.url?.startsWith("https:")) {
      setAttachmentUrl(attachment.url);
    }
  }, [attachment?.url]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      aria-labelledby={t("attachment_view")}
      sx={{
        "& .MuiDialog-paper": {
          margin: 0,
          maxHeight: "none",
          maxWidth: "none",
          borderRadius: 0,
          backgroundColor: "rgba(0, 0, 0, 0.95)",
        },
      }}
    >
      {/* Fixed close button at top right */}
      <Box
        sx={{
          position: "fixed",
          top: 20,
          right: 20,
          zIndex: 1400,
          backgroundColor: "rgba(255, 255, 255, 0.2)",
          borderRadius: "50%",
          backdropFilter: "blur(5px)",
          "&:hover": {
            backgroundColor: "rgba(255, 255, 255, 0.3)",
          },
        }}
      >
        <IconButton
          onClick={onClose}
          aria-label={t("close_attachment")}
          sx={{
            color: "white",
            fontSize: "24px",
            "&:hover": {
              backgroundColor: "transparent",
            },
          }}
        >
          <CloseIcon fontSize="large" />
        </IconButton>
      </Box>

      <DialogContent
        sx={{
          padding: 0,
          height: "100vh",
          width: "100vw",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "rgba(0, 0, 0, 0.95)",
          position: "relative",
          overflow: "hidden",
        }}
        onClick={onClose} // Click outside to close
      >
        {attachment?.type === "video" ? (
          <video
            src={attachment?.url}
            controls
            onClick={(e) => e.stopPropagation()} // Prevent closing when clicking on video
            style={{
              width: "95vw",
              height: "95vh",
              objectFit: "contain",
              borderRadius: "8px",
            }}
          />
        ) : attachment?.url?.startsWith("data:") ? (
          <img
            src={attachment?.url}
            alt={t("attachment_image", {
              name: attachment?.name || attachment?.fileName,
            })}
            onClick={(e) => e.stopPropagation()} // Prevent closing when clicking on image
            style={{
              width: "95vw",
              height: "95vh",
              objectFit: "contain",
              cursor: "zoom-in",
              borderRadius: "8px",
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.5)",
            }}
          />
        ) : (
          <>
            {isLoading ? (
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  color: "white",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <CircularProgress sx={{ color: "white" }} size={60} />
                <Box sx={{ color: "white", fontSize: "16px" }}>
                  {t("Loading attachment...")}
                </Box>
              </Box>
            ) : (
              attachmentUrl && (
                <img
                  src={attachmentUrl}
                  alt={t("attachment_image", {
                    name: attachment?.name || attachment?.fileName,
                  })}
                  onClick={(e) => e.stopPropagation()} // Prevent closing when clicking on image
                  style={{
                    width: "95vw",
                    height: "95vh",
                    objectFit: "contain",
                    cursor: "zoom-in",
                    borderRadius: "8px",
                    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.5)",
                  }}
                />
              )
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AttachmentViewModal;
